import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/apiClient";
import { formatDateTimeShort } from "@/lib/bookingDates";
import { isZvtConfigured, type DeviceServiceSettings } from "@/lib/deviceService";
import { formatCurrency, parseAmount } from "@/lib/money";
import { isInvoiceFinalized } from "@/lib/invoiceStatus";
import {
  PAYMENT_METHODS,
  PAYMENT_METHOD_LABELS,
  type PaymentMethod,
} from "@/lib/paymentMethod";
import { GuestTabletPaymentConfirmBanner } from "@/components/bookings/GuestTabletPaymentConfirmBanner";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import type { Invoice, Payment, PaymentCreatePayload } from "@/types/camping";
import type { GuestTabletPendingPayment } from "@/types/guestTablet";

function todayLocalDate(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function localDateToIso(dateStr: string): string {
  return `${dateStr}T12:00:00.000Z`;
}


function openAmountNumber(invoice: Invoice): number {
  const raw = invoice.open_amount ?? invoice.total_amount;
  const n = Number(raw);
  return Number.isFinite(n) ? n : 0;
}

export interface InvoicePaymentsSectionProps {
  invoice: Invoice;
  canRecord: boolean;
  onPaymentRecorded?: () => void;
  /** Ausstehende Tablet-Zahlung (Bar/EC) – wird im Bereich Zahlungen angezeigt */
  pendingTabletPayment?: GuestTabletPendingPayment | null;
  confirmingTabletPayment?: boolean;
  onConfirmTabletPayment?: () => void;
  /** Summe „bezahlt / offen“ ausblenden, wenn sie woanders schon steht. */
  showSummary?: boolean;
}

export function InvoicePaymentsSection({
  invoice,
  canRecord,
  onPaymentRecorded,
  pendingTabletPayment = null,
  confirmingTabletPayment = false,
  onConfirmTabletPayment,
  showSummary = true,
}: InvoicePaymentsSectionProps) {
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [amount, setAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>("cash");
  const [paidDate, setPaidDate] = useState(todayLocalDate());
  const [reference, setReference] = useState("");
  const [notes, setNotes] = useState("");
  const [deviceService, setDeviceService] = useState<DeviceServiceSettings | null>(null);
  const [manualCardEntry, setManualCardEntry] = useState(false);

  const showSection = isInvoiceFinalized(invoice.status);
  const zvtConfigured = isZvtConfigured(deviceService);
  const useTerminal =
    paymentMethod === "card" && zvtConfigured && !manualCardEntry;
  const open = openAmountNumber(invoice);
  const canPay = canRecord && open > 0 && invoice.status !== "cancelled";

  const loadPayments = useCallback(() => {
    if (!showSection) return;
    setLoading(true);
    setError(null);
    api
      .get<Payment[]>(`/api/v1/invoices/${invoice.id}/payments`)
      .then(setPayments)
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, [invoice.id, showSection]);

  useEffect(() => {
    loadPayments();
  }, [loadPayments]);

  useEffect(() => {
    api
      .get<{ device_service?: DeviceServiceSettings }>("/api/v1/settings")
      .then((settings) => setDeviceService(settings.device_service ?? null))
      .catch(() => setDeviceService(null));
  }, []);

  function openRecordModal() {
    setAmount(open > 0 ? String(open) : "");
    setPaymentMethod("cash");
    setPaidDate(todayLocalDate());
    setReference("");
    setNotes("");
    setManualCardEntry(false);
    setError(null);
    setModalOpen(true);
  }

  async function submitPayment() {
    const parsed = parseAmount(amount);
    if (parsed == null || parsed <= 0) {
      setError("Bitte einen gültigen Betrag größer 0 eingeben.");
      return;
    }
    if (parsed > open + 0.0001) {
      setError(`Der Betrag darf den offenen Restbetrag (${formatCurrency(String(open), invoice.currency)}) nicht übersteigen.`);
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const payload: PaymentCreatePayload = {
        invoice_id: invoice.id,
        amount: parsed.toFixed(2),
        payment_method: paymentMethod,
        paid_at: localDateToIso(paidDate),
        use_terminal: useTerminal,
      };
      const ref = reference.trim();
      const note = notes.trim();
      if (!useTerminal && ref) payload.reference = ref;
      if (!useTerminal && note) payload.notes = note;

      await api.post<Payment>("/api/v1/payments", payload);
      setModalOpen(false);
      loadPayments();
      onPaymentRecorded?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Zahlung konnte nicht verbucht werden.");
    } finally {
      setSubmitting(false);
    }
  }

  if (!showSection) return null;

  return (
    <div className="mt-3 border-t border-separator pt-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h4 className="text-sm font-semibold text-label">Zahlungen</h4>
        {canPay ? (
          <Button type="button" variant="secondary" size="sm" onClick={openRecordModal}>
            Zahlung erfassen
          </Button>
        ) : null}
      </div>

      {showSummary && (invoice.paid_amount != null || invoice.open_amount != null) ? (
        <p className="mt-1 text-xs text-label-secondary tabular-nums">
          Bezahlt: {formatCurrency(invoice.paid_amount ?? "0", invoice.currency)}
          {" · "}
          Offen: {formatCurrency(invoice.open_amount ?? "0", invoice.currency)}
        </p>
      ) : null}

      {pendingTabletPayment && onConfirmTabletPayment ? (
        <div className="mt-3">
          <GuestTabletPaymentConfirmBanner
            pendingPayment={pendingTabletPayment}
            currency={invoice.currency}
            confirming={confirmingTabletPayment}
            onConfirm={onConfirmTabletPayment}
          />
        </div>
      ) : null}

      {error && !modalOpen ? <p className="mt-2 text-xs text-status-red">{error}</p> : null}
      {loading ? (
        <p className="mt-2 text-xs text-label-tertiary">Lade Zahlungen…</p>
      ) : payments.length === 0 ? (
        <p className="mt-2 text-xs text-label-tertiary">Noch keine Zahlungen verbucht.</p>
      ) : (
        <ul className="mt-2 space-y-1.5">
          {payments.map((p) => (
            <li
              key={p.id}
              className="flex items-center justify-between gap-3 rounded-lg bg-fill-tertiary/70 px-3 py-2"
            >
              <span className="min-w-0">
                <span className="block text-sm text-label">
                  {PAYMENT_METHOD_LABELS[p.payment_method as PaymentMethod] ?? p.payment_method}
                </span>
                <span className="mt-0.5 block text-xs text-label-secondary">
                  {formatDateTimeShort(p.paid_at)}
                  {p.reference ? ` · ${p.reference}` : ""}
                </span>
              </span>
              <span className="shrink-0 tabular-nums text-sm font-semibold text-label">
                {formatCurrency(p.amount, invoice.currency)}
              </span>
            </li>
          ))}
        </ul>
      )}

      <Modal
        open={modalOpen}
        onClose={() => !submitting && setModalOpen(false)}
        title="Zahlung erfassen"
        description={`Rechnung ${invoice.invoice_number} · offen ${formatCurrency(String(open), invoice.currency)}`}
        size="md"
      >
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            void submitPayment();
          }}
        >
          <label className="flex flex-col gap-1 text-sm">
            Betrag ({invoice.currency})
            <input
              type="number"
              min="0.01"
              step="0.01"
              max={open}
              required
              className="eh-input tabular-nums"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
          </label>

          <label className="flex flex-col gap-1 text-sm">
            Zahlungsart
            <select
              className="eh-input"
              value={paymentMethod}
              onChange={(e) => {
                setPaymentMethod(e.target.value as PaymentMethod);
                setManualCardEntry(false);
              }}
            >
              {PAYMENT_METHODS.map((m) => (
                <option key={m} value={m}>
                  {PAYMENT_METHOD_LABELS[m]}
                </option>
              ))}
            </select>
          </label>

          {useTerminal ? (
            <p className="rounded-md border border-separator bg-fill-tertiary px-3 py-2 text-sm text-label-secondary">
              Die Zahlung wird am EC-Terminal ausgelöst. Bitte Karte am Terminal vorhalten.
            </p>
          ) : null}

          {paymentMethod === "card" && zvtConfigured ? (
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                className="rounded border-separator text-accent focus:ring-accent"
                checked={manualCardEntry}
                onChange={(e) => setManualCardEntry(e.target.checked)}
              />
              Ohne Terminal verbuchen (manuell)
            </label>
          ) : null}

          <label className="flex flex-col gap-1 text-sm">
            Zahlungsdatum
            <input
              type="date"
              required
              className="eh-input"
              value={paidDate}
              onChange={(e) => setPaidDate(e.target.value)}
            />
          </label>

          {!useTerminal ? (
            <>
              <label className="flex flex-col gap-1 text-sm">
                Referenz <span className="text-label-tertiary">(optional)</span>
                <input
                  type="text"
                  className="eh-input"
                  maxLength={128}
                  value={reference}
                  onChange={(e) => setReference(e.target.value)}
                  placeholder="z. B. Transaktions-ID"
                />
              </label>

              <label className="flex flex-col gap-1 text-sm">
                Notiz <span className="text-label-tertiary">(optional)</span>
                <textarea
                  className="eh-input min-h-[2.5rem] resize-y"
                  rows={2}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
              </label>
            </>
          ) : null}

          {error ? <p className="text-sm text-status-red">{error}</p> : null}

          <div className="flex flex-wrap justify-end gap-2 pt-1">
            <Button
              type="button"
              variant="secondary"
              disabled={submitting}
              onClick={() => setModalOpen(false)}
            >
              Abbrechen
            </Button>
            <Button type="submit" loading={submitting} disabled={submitting}>
              {useTerminal ? "Am EC-Terminal zahlen" : "Zahlung verbuchen"}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
